import { Pause, Play, Volume2, VolumeX, X } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalAudio } from '../../store';

const formatTime = (timeSeconds: number): string => {
  if (!Number.isFinite(timeSeconds) || timeSeconds <= 0) return '0:00';
  const totalSeconds = Math.floor(timeSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export default function GlobalAudioMiniPlayer() {
  const navigate = useNavigate();
  const audio = useGlobalAudio();

  const hasTrack = Boolean(audio.track?.src);
  const shouldShow = audio.enabled && hasTrack;

  const progressPercent = useMemo(() => {
    if (!audio.duration || audio.duration <= 0) return 0;
    return Math.min(100, Math.max(0, (audio.currentTime / audio.duration) * 100));
  }, [audio.currentTime, audio.duration]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-3 z-[95] px-3 sm:bottom-4 sm:px-4">
      <div className="mx-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white/95 shadow-lg backdrop-blur">
        <div className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
          {audio.track?.coverUrl ? (
            <button
              type="button"
              onClick={() => {
                if (audio.track?.productId) {
                  void navigate(`/products/${audio.track.productId}`);
                }
              }}
              className="h-10 w-10 overflow-hidden rounded-xl bg-slate-100"
              aria-label="Open product"
              title="Open product"
            >
              <img src={audio.track.coverUrl} alt={audio.track.title} className="h-full w-full object-cover" />
            </button>
          ) : (
            <div className="h-10 w-10 rounded-xl bg-slate-100" />
          )}

          <button
            type="button"
            onClick={() => {
              if (audio.track?.productId) {
                void navigate(`/products/${audio.track.productId}`);
              }
            }}
            className="min-w-0 flex-1 text-left"
            title={audio.track?.title}
          >
            <p className="truncate text-sm font-semibold text-slate-900">{audio.track?.title}</p>
            <p className="mt-0.5 text-xs text-slate-500">
              {formatTime(audio.currentTime)} / {audio.duration ? formatTime(audio.duration) : '--:--'}
            </p>
          </button>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void audio.togglePlayPause()}
              className="rounded-full border border-slate-200 bg-white p-2 text-slate-800 shadow-sm transition hover:bg-slate-50"
              aria-label={audio.isPlaying ? 'Pause audio' : 'Play audio'}
            >
              {audio.isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => audio.setMuted(!audio.muted)}
              className="rounded-full border border-slate-200 bg-white p-2 text-slate-700 shadow-sm transition hover:bg-slate-50"
              aria-label={audio.muted ? 'Unmute audio' : 'Mute audio'}
            >
              {audio.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => {
                audio.stop();
                audio.setEnabled(false);
              }}
              className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-700"
              aria-label="Stop background audio"
              title="Stop"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="h-1 w-full bg-slate-100">
          <div className="h-full bg-orange-500 transition-[width] duration-200" style={{ width: `${progressPercent}%` }} />
        </div>
      </div>
    </div>
  );
}

